import { createIntroPages, createGenericPages } from '../pages/generic'
import { PageDescriptor } from '../pages/types'
import Architecture from './Architecture'
import { Brick } from './bricks'
import { Scenario } from './scenarios/data'

export type Section = {
    title: string
    key: string
    brick?: Brick
    pages: PageDescriptor[]
}

export type PageHandle = {
    section: string
    page: string
}

export class Guide {
    constructor(readonly sections: Section[]) {

    }

    static Create(scenario: Scenario, architecture: Architecture) {
        let sections: Section[] = []
        const introSelection = Array.from(createIntroPages(scenario))
        if(introSelection.length > 0) [
            sections.push({ title: 'Welcome to Gurobi!', key: 'intro', pages: introSelection })
        ]
        for(const brick of architecture.bricks) {
            sections.push({ title: brick.title, key: brick.key(), brick, pages: Array.from(brick.getPages()) })
        }
        const genericSelection = Array.from(createGenericPages(scenario))
        if(genericSelection.length > 0) {
            sections.push({ title: 'More information', key: 'general', pages: genericSelection })
        }
        return new Guide(sections)
    }

    getSection(key: string) {
        for(const section of this.sections) {
            if(section.key == key) {
                return section
            }
        }
        return undefined
    }
    

    pagePosition(current: PageHandle): [number, number] | undefined {
        const sectionIndex = this.sections.findIndex(s => s.key == current.section)
        if(sectionIndex < 0) {
            return undefined
        }

        const pageIndex = this.sections[sectionIndex].pages.findIndex(p => p.key == current.page)
        if(pageIndex < 0) {
            return undefined
        }

        return [sectionIndex, pageIndex]
    }

    nextPage(current: PageHandle): PageHandle|undefined {
        const sections = this.sections
        const pos = this.pagePosition(current)
        if(pos == undefined) {
            return this.defaultPageHandle()
        }
        const [sectionIndex, pageIndex] = pos
        const section = sections[sectionIndex]
        
        if(section.pages.length > pageIndex + 1) {
            return { section: current.section, page: section.pages[pageIndex + 1].key }
        }

        if(this.sections.length > sectionIndex + 1) {
            return { section: sections[sectionIndex + 1].key, page: sections[sectionIndex + 1].pages[0].key }
        }

        return undefined
    }

    previousPage(current: PageHandle): PageHandle|undefined {
        const sections = this.sections
        const pos = this.pagePosition(current)
        if(pos == undefined) {
            return this.defaultPageHandle()
        }
        const [sectionIndex, pageIndex] = pos
        const section = sections[sectionIndex]

        if(pageIndex > 0) {
            return { section: section.key, page: section.pages[pageIndex - 1].key }
        }

        if(sectionIndex > 0) {
            const prevSection = sections[sectionIndex - 1]
            return { section: prevSection.key, page: prevSection.pages[prevSection.pages.length - 1].key }
        }

        return undefined
    }

    validatePageHandle(handle: PageHandle|undefined): PageHandle {
        if(handle == undefined) {
            return this.defaultPageHandle()
        }

        const page = this.getPage(handle)
        if(page == undefined) {
            return this.defaultPageHandle()
        }

        return handle
    }

    defaultPageHandle(): PageHandle {
        for(const section of this.sections) {
            if(section.pages.length > 0) {
                return { section: section.key, page: section.pages[0].key }
            }
        }

        throw new Error('Guide without pages detected')
    }

    getPage(current?: PageHandle): [Section, PageDescriptor]|undefined {
        if(current == undefined) {
            return undefined
        }

        const section = this.sections.find(s => s.key == current.section)
        if(section == undefined) {
            return undefined
        }

        const page = section.pages.find(p => p.key == current.page)
        return page ? [section, page] : undefined
    }

    getFirstBrickPage(brick: string): PageHandle|undefined {
        for(const section of this.sections) {
            if(section.brick?.key() == brick) {
                return { section: section.key, page: section.pages[0].key }
            }
        }
        return undefined
    }
}

export const packPageHandle = (handle?: PageHandle): string => handle ? (handle.section + '.' + handle.page) : ''

export const unpackPageHandle = (packed: string): PageHandle|undefined => {
    const regex = /(.*)\.(.*)/i
    const found = packed.match(regex)
    if(!found) {
        return undefined
    }
    return { section: found[1], page: found[2] }
}
